import {
  createProfessionalProfile,
  getProfileUtils,
  getUserProps,
  userLocation,
} from '@pharmaplan/common';
import type {
  CreateProfessionalProfileParams,
  IAiOnboardingFinalProfileFields,
} from '@pharmaplan/common';
import { useCallback } from 'react';

import { useAppDispatch } from '../useAppDispatch';
import { useAppSelector } from '../useAppSelector';

import { mergeSoftwareCatalog } from '../../components/SignupFlow/AiCompanion/profileSubmission';

interface IUseAiProfileSubmission {
  getConversationId: () => string | null;
  getProfilePictureFile: () => File | null;
}

const useAiProfileSubmission = ({
  getConversationId,
  getProfilePictureFile,
}: IUseAiProfileSubmission) => {
  const dispatch = useAppDispatch();
  const location = useAppSelector(userLocation);
  const profileUtilities = useAppSelector(getProfileUtils);
  const userProps = useAppSelector(getUserProps);

  return useCallback(
    async (
      profile: IAiOnboardingFinalProfileFields,
      editedProps: string[],
    ) => {
      const {
        language: signupLanguage,
        userType: signupRole,
        ...signupMetadata
      } = userProps;
      const firstName = profile.firstName.trim() || null;
      const lastName = profile.lastName.trim() || null;

      if (signupRole && signupRole !== profile.role) {
        throw new Error('AI onboarding role does not match signup role');
      }

      if (!firstName || !lastName) {
        throw new Error('AI onboarding requires first and last names');
      }

      const selectedSoftwares = mergeSoftwareCatalog(
        profile.selectedSoftwares,
        profileUtilities?.softwares ?? [],
      );
      const profilePicture = getProfilePictureFile();

      if (!profilePicture) {
        throw new Error('AI onboarding profile picture is required');
      }

      const conversationId = getConversationId();

      if (!conversationId) {
        throw new Error('AI onboarding conversation ID is required');
      }

      const form = new FormData();
      form.append('image', profilePicture);
      const request: CreateProfessionalProfileParams & typeof signupMetadata = {
        ...signupMetadata,
        ...profile,
        firstName,
        lastName,
        conversationId,
        editedProps,
        form,
        isAiOnboarding: true,
        isPictureUploaded: true,
        latitude: profile.latitude ?? location?.latitude ?? null,
        longitude: profile.longitude ?? location?.longitude ?? null,
        preferdLanguage: profile.preferdLanguage || signupLanguage || '',
        prefferedDistanceRange: profile.preferredDistanceRange ?? null,
        role: profile.role,
        selectedSoftwares,
      };
      const result = await dispatch(createProfessionalProfile(request));

      if (
        !createProfessionalProfile.fulfilled.match(result)
        || result.payload === undefined
        || result.payload === null
      ) {
        throw new Error('Unable to create the AI professional profile');
      }
    },
    [
      dispatch,
      getConversationId,
      getProfilePictureFile,
      location?.latitude,
      location?.longitude,
      profileUtilities?.softwares,
      userProps,
    ],
  );
};

export default useAiProfileSubmission;
